import { ICON_SIZE } from '@components/v2/constants';
import {
  checkboxCheckedIcon,
  checkboxIcon,
  checkboxIndeterminateIcon,
} from '@components/v2/form/Checkbox/Checkbox.icons';
import { radioCheckedIcon, radioIcon } from '@components/v2/form/Radio/Radio.icons';
import { Shadows, Theme, ThemeOptions, alpha, darken } from '@mui/material/styles';

const FONT_FAMILY = "'Nunito Sans', sans-serif";
const INPUT_LABEL_SHRINK_SCALE = 0.8;
const LIST_ITEM_HORIZONTAL_PADDING = 0.75;
const BUTTON_PADDING_INLINE: Record<string, number> = {
  small: 1.25,
  medium: 1.5,
  large: 2,
};

const outlinedInputPadding = (theme: Theme) => theme.spacing(0.85, 1.5);
const outlinedInputPaddingSmall = (theme: Theme) => theme.spacing(0.6, 1.5);
const outlinedInputPaddingLarge = (theme: Theme) => theme.spacing(1.35, 1.5);

const normalizedLineHeight = {
  lineHeight: 'normal',
  '& .MuiTypography-root': {
    lineHeight: 'normal',
  },
};

const menuPaperStyles = (theme: Theme) => ({
  borderRadius: theme.shape.borderRadiusXl,
  border: `1px solid ${theme.palette.custom.border.primary}`,
  marginBlock: theme.spacing(0.5),
});

const menuListStyles = (theme: Theme) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  gap: theme.spacing(0.5),
});

export const gradientBorder = (fill: string, border: string) =>
  `linear-gradient(${fill}, ${fill}) padding-box, ${border} border-box`;

const secondaryButtonStyles = (theme: Theme) => {
  const { secondaryButton, secondaryButtonHover } = theme.palette.custom.gradients;

  return {
    border: '1px solid transparent',
    background: gradientBorder(theme.palette.secondary.main, secondaryButton),
    '&:hover': {
      background: gradientBorder(theme.palette.secondary.light, secondaryButtonHover),
    },
    '&:active': {
      background: gradientBorder(theme.palette.secondary.dark, secondaryButtonHover),
    },
    '&[data-active="true"]': {
      background: gradientBorder(theme.palette.secondary.dark, secondaryButton),
      '&:hover': {
        background: gradientBorder(
          darken(theme.palette.secondary.dark, 0.05),
          secondaryButtonHover,
        ),
      },
      '&:active': {
        background: gradientBorder(darken(theme.palette.secondary.dark, 0.1), secondaryButtonHover),
      },
    },
  };
};

const listItemStyles = (theme: Theme) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(LIST_ITEM_HORIZONTAL_PADDING),
  transition: theme.transitions.create(['background-color', 'border-radius'], {
    duration: theme.transitions.duration.shorter,
  }),
  backgroundColor: 'transparent',
  minHeight: '28px',
  flexShrink: 0,
  '&:hover': { backgroundColor: theme.palette.action.hover },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    '&:hover': { backgroundColor: theme.palette.action.selected },
  },
  '& .MuiListItemIcon-root': { minWidth: 0 },
});

export const baseThemeOptions: ThemeOptions = {
  shadows: ['none', ...Array(24).fill('0px 4px 16px -6px rgba(0,0,0,0.25)')] as Shadows,
  shape: {
    borderRadius: 8,
    borderRadiusXs: 2,
    borderRadiusSm: 4,
    borderRadiusMd: 6,
    borderRadiusLg: 8,
    borderRadiusXl: 12,
    borderRadius2xl: 16,
    borderRadius3xl: 32,
    borderRadiusFull: 9999,
  },
  typography: {
    fontFamily: FONT_FAMILY,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: {
      fontSize: '26px',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    h2: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    h3: {
      fontSize: '22px',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    h4: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    h5: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    h6: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: 1.0,
      letterSpacing: 0,
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    body1: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    body2: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    caption: {
      fontSize: '11px',
      fontWeight: 400,
      lineHeight: 1.0,
      letterSpacing: 0,
    },
    overline: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: '1px',
      textTransform: 'uppercase',
    },
    button: {
      fontWeight: 400,
      textTransform: 'none',
    },
    micro1: {
      fontFamily: FONT_FAMILY,
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    micro2: {
      fontFamily: FONT_FAMILY,
      fontSize: '11px',
      fontWeight: 300,
      lineHeight: 1.2,
      letterSpacing: 0,
    },
  },
  components: {
    MuiBadge: {
      styleOverrides: {
        badge: ({ theme }) => ({
          height: 14,
          minWidth: 14,
          padding: `0px ${theme.spacing(0.5)}`,
          fontSize: theme.typography.micro2.fontSize,
          borderRadius: '999px',
          '&.MuiBadge-dot': {
            width: 8,
            height: 8,
            minWidth: 'unset',
            padding: 0,
            borderRadius: '50%',
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          ...normalizedLineHeight,
          borderRadius:
            ownerState.color === 'secondary'
              ? theme.shape.borderRadiusMd
              : theme.shape.borderRadiusFull,
        }),
        labelMedium: ({ theme }) => ({
          fontSize: theme.typography.body1.fontSize,
          paddingInline: theme.spacing(1),
        }),
        labelSmall: ({ theme }) => ({
          fontSize: theme.typography.body2.fontSize,
          paddingInline: theme.spacing(1),
        }),
        sizeMedium: {
          height: 22,
        },
        sizeSmall: {
          height: 18,
        },
        deleteIcon: ({ theme }) => ({
          color: theme.palette.accent.light,
          '&:hover': { color: theme.palette.primary.main },
        }),
      },
      variants: [
        {
          props: { size: 'extraSmall' },
          style: ({ theme }: { theme: Theme }) => ({
            height: 16,
            paddingBlock: theme.spacing(0.25),
            paddingInline: theme.spacing(0.5),
            '& .MuiChip-label': {
              fontSize: theme.typography.micro1.fontSize,
              paddingInline: theme.spacing(0.5),
            },
            '& .MuiChip-icon': {
              marginLeft: 0,
              marginRight: 0,
            },
          }),
        },
        {
          props: { color: 'default' },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.secondary.light,
            '&.MuiChip-clickable:hover': {
              backgroundColor: theme.palette.secondary.dark,
            },
          }),
        },
        {
          props: { color: 'secondary' },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.secondary.main,
            boxShadow: `inset 0 0 0 0.5px ${theme.palette.custom.border.primary}`,
            '&:hover': {
              backgroundColor: theme.palette.secondary.main,
            },
          }),
        },
      ],
    },
    MuiButtonBase: {
      defaultProps: { disableRipple: true },
    },
    MuiSlider: {
      styleOverrides: {
        thumb: {
          '&:hover, &.Mui-active': {
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        body: {
          '& a': {
            color: 'inherit',
            textDecoration: 'none',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, shape: 'round' },
      styleOverrides: {
        sizeSmall: ({ theme }) => ({
          fontSize: theme.typography.micro1.fontSize,
          paddingInline: theme.spacing(BUTTON_PADDING_INLINE.small),
          paddingBlock: theme.spacing(0.5),
          height: 22,
        }),
        sizeMedium: ({ theme }) => ({
          fontSize: theme.typography.subtitle2.fontSize,
          paddingInline: theme.spacing(BUTTON_PADDING_INLINE.medium),
          height: 28,
        }),
        sizeLarge: ({ theme }) => ({
          fontSize: theme.typography.subtitle2.fontSize,
          paddingInline: theme.spacing(BUTTON_PADDING_INLINE.large),
          height: 32,
        }),
        endIcon: ({ ownerState, theme }) => ({
          marginLeft: ownerState.loading ? theme.spacing(1) : undefined,
        }),
        loadingIndicator: ({ ownerState, theme }) =>
          ownerState.fullWidth
            ? {}
            : { right: theme.spacing(BUTTON_PADDING_INLINE[ownerState.size ?? 'medium'] ?? 1.5) },
        iconSizeSmall: { '& > *:first-of-type': { width: ICON_SIZE.xs, height: ICON_SIZE.xs } },
        iconSizeMedium: { '& > *:first-of-type': { width: ICON_SIZE.md, height: ICON_SIZE.md } },
        iconSizeLarge: { '& > *:first-of-type': { width: ICON_SIZE.md, height: ICON_SIZE.md } },
        containedSecondary: ({ theme }) => ({
          color: theme.palette.text.secondary,
          ...secondaryButtonStyles(theme),
          '&:hover': {
            ...secondaryButtonStyles(theme)['&:hover'],
            color: theme.palette.text.primary,
          },
          '&[data-active="true"]': {
            ...secondaryButtonStyles(theme)['&[data-active="true"]'],
            color: theme.palette.text.primary,
          },
          '&.Mui-disabled': { backgroundColor: theme.palette.secondary.main },
        }),
        root: ({ theme, ownerState }) => ({
          ...normalizedLineHeight,
          fontWeight: 500,
          borderRadius:
            ownerState.shape === 'square' ? theme.shape.borderRadius : theme.shape.borderRadiusFull,
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
          '&.Mui-focusVisible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
          '&.Mui-focusVisible .MuiTouchRipple-ripplePulsate': {
            display: 'none',
          },
        }),
      },
      variants: [
        {
          props: { variant: 'text' },
          style: ({ theme }: { theme: Theme }) => ({
            '&[data-active="true"]': { backgroundColor: theme.palette.secondary.dark },
          }),
        },
        {
          props: { variant: 'text', color: 'secondary' },
          style: ({ theme }: { theme: Theme }) => ({
            color: theme.palette.text.secondary,
            backgroundColor: 'transparent',
            '&:hover': {
              color: theme.palette.text.primary,
              backgroundColor: alpha(theme.palette.accent.main, 0.04),
            },
            '&:active': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.action.selected,
            },
            '&[data-active="true"]': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.action.selected,
            },
          }),
        },
        {
          props: { color: 'secondary' },
          style: ({ theme }: { theme: Theme }) => ({
            '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.accent.main}` },
          }),
        },
        {
          props: { color: 'error' },
          style: ({ theme }: { theme: Theme }) => ({
            '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.error.main}` },
          }),
        },
      ],
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
        focusVisibleClassName: 'Mui-focusVisible',
        size: 'large',
      },
      styleOverrides: {
        root: {
          width: 40,
          height: 20,
          padding: 0,
        },
        switchBase: ({ theme }) => ({
          padding: 0,
          margin: '2px 3px',
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: theme.palette.primary.contrastText,
            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              backgroundColor: theme.palette.action.disabled,
              opacity: 1,
            },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            border: `4px solid ${theme.palette.primary.contrastText}`,
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            backgroundColor: theme.palette.action.disabled,
            opacity: 1,
          },
        }),
        thumb: ({ theme }) => ({
          boxSizing: 'border-box',
          width: 16,
          height: 16,
          backgroundColor: theme.palette.primary.contrastText,
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.10)',
        }),
        sizeMedium: {
          width: 30,
          height: 16,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: '2px 3px',
            '&.Mui-checked': {
              transform: 'translateX(12px)',
            },
          },
          '& .MuiSwitch-thumb': {
            width: 12,
            height: 12,
          },
        },
        sizeSmall: {
          width: 22,
          height: 12,
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: '2px 2px',
            '&.Mui-checked': {
              transform: 'translateX(10px)',
            },
          },
          '& .MuiSwitch-thumb': {
            width: 8,
            height: 8,
          },
        },
        track: ({ theme }) => ({
          borderRadius: 13,
          backgroundColor: theme.palette.action.active,
          opacity: 1,
          transition: theme.transitions.create(['background-color'], { duration: 500 }),
        }),
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        icon: checkboxIcon,
        checkedIcon: checkboxCheckedIcon,
        indeterminateIcon: checkboxIndeterminateIcon,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '--form-indicator-color': theme.palette.primary.contrastText,
          color: theme.palette.action.active,
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.accent.main,
          },
          '&.Mui-checked, &.MuiCheckbox-indeterminate': {
            color: theme.palette.primary.main,
            '&.Mui-disabled': { color: theme.palette.text.disabled },
          },
          aspectRatio: '1',
        }),
        sizeMedium: {
          '& .MuiSvgIcon-root': { fontSize: '16px' },
          width: 32,
          height: 32,
        },
        sizeSmall: {
          '& .MuiSvgIcon-root': { fontSize: '14px' },
          width: 24,
          height: 24,
        },
      },
    },
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        icon: radioIcon,
        checkedIcon: radioCheckedIcon,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '--form-indicator-color': theme.palette.primary.contrastText,
          color: theme.palette.action.active,
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.accent.main,
          },
          '&.Mui-checked': {
            color: theme.palette.primary.main,
          },
          aspectRatio: '1',
          '& .MuiSvgIcon-root': { fontSize: '16px' },
          width: 28,
          height: 28,
          '&.MuiRadio-sizeSmall': {
            '& .MuiSvgIcon-root': { fontSize: '13px' },
            width: 24,
            height: 24,
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:has(.MuiSwitch-root)': { gap: theme.spacing(1), marginLeft: 0 },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-focused:not(.Mui-error)': {
            color: theme.palette.accent.main,
          },
        }),
      },
    },
    MuiIconButton: {
      defaultProps: { shape: 'square' },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          borderRadius:
            ownerState.shape === 'square' ? theme.shape.borderRadius : theme.shape.borderRadiusFull,
          color: theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
          },
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
        }),
        colorSecondary: ({ theme }) => ({
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.text.secondary,
          ...secondaryButtonStyles(theme),
          '&:hover': {
            ...secondaryButtonStyles(theme)['&:hover'],
            color: theme.palette.text.primary,
          },
          '&[data-active="true"]': {
            ...secondaryButtonStyles(theme)['&[data-active="true"]'],
            color: theme.palette.text.primary,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            borderColor: theme.palette.action.disabled,
          },
        }),
        sizeSmall: { width: 22, height: 22, padding: 0 },
        sizeMedium: { width: 28, height: 28, padding: 0 },
        sizeLarge: { width: 32, height: 32, padding: 0 },
      },
      variants: [
        {
          props: { color: 'accent' },
          style: ({ theme }: { theme: Theme }) => ({
            backgroundColor: theme.palette.accent.main,
            color: theme.palette.accent.contrastText,
            '&:hover': {
              color: theme.palette.accent.contrastText,
              backgroundColor: theme.palette.accent.dark,
            },
            '&:active': {
              color: theme.palette.accent.contrastText,
              backgroundColor: darken(theme.palette.accent.dark, 0.45),
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
            },
          }),
        },
        {
          props: { color: 'primary' },
          style: ({ theme }: { theme: Theme }) => ({
            '&[data-active="true"]': { backgroundColor: theme.palette.secondary.dark },
          }),
        },
      ],
    },
    MuiList: {
      defaultProps: {
        disablePadding: true,
        dense: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(0.5),
          '& .MuiDivider-root': {
            marginTop: theme.spacing(0.5),
          },
        }),
      },
    },
    MuiListSubheader: {
      defaultProps: {
        disableSticky: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(0, LIST_ITEM_HORIZONTAL_PADDING, 0.5),
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          fontSize: theme.typography.body2.fontSize,
          fontWeight: theme.typography.fontWeightRegular,
          lineHeight: theme.typography.body2.lineHeight,
          letterSpacing: theme.typography.body2.letterSpacing,
          color: theme.palette.text.tertiary,
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: 0,
          width: ICON_SIZE.md,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.palette.accent.dark,
          marginRight: theme.spacing(1),
          '& svg': {
            width: ICON_SIZE.md,
            height: ICON_SIZE.md,
          },
        }),
      },
    },
    MuiListItemAvatar: {
      styleOverrides: {
        root: ({ theme }) => ({
          minWidth: 0,
          flexShrink: 0,
          marginRight: theme.spacing(1),
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiListItemText-primary': {
            fontWeight: theme.typography.fontWeightRegular,
            color: theme.palette.text.primary,
            fontSize: theme.typography.body1.fontSize,
            lineHeight: theme.typography.body1.lineHeight,
          },
          '& .MuiListItemText-secondary': {
            marginTop: theme.spacing(0.25),
          },
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...listItemStyles(theme),
          marginLeft: 0,
          '&:has(.MuiCheckbox-root)': {
            padding: theme.spacing(0.5),
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.09) },
            },
          },
        }),
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...listItemStyles(theme),
          '&:has(.MuiCheckbox-root)': {
            padding: theme.spacing(0.5),
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.09) },
            },
          },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: ({ theme }) => menuPaperStyles(theme),
        list: ({ theme }) => ({
          ...menuListStyles(theme),
          padding: theme.spacing(0.75),
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: ({ theme }) => ({
          width: '16px',
          height: '16px',
          color: theme.palette.text.secondary,
          translate: '-2px -1px',
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover:not(.Mui-disabled):not(.Mui-error):not(.Mui-focused)': {
            color: theme.palette.text.secondary,
          },
          '&.Mui-focused:not(.Mui-error)': {
            color: theme.palette.accent.main,
          },
        }),
        outlined: ({ theme }) => ({
          '&:not(.MuiInputLabel-shrink)': {
            transform: `translate(${theme.spacing(1.5)}, ${theme.spacing(0.85)}) scale(1)`,
          },
          '&.MuiInputLabel-shrink': {
            fontSize: theme.typography.micro2.fontSize,
            transform: `translate(${theme.spacing(1.5)}, -9px) scale(1)`,
          },
        }),
        sizeSmall: ({ theme }) => ({
          '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
            transform: `translate(${theme.spacing(1.5)}, ${theme.spacing(0.6)}) scale(1)`,
          },
          '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
            fontSize: theme.typography.micro2.fontSize,
            transform: `translate(${theme.spacing(1.5)}, -9px) scale(1)`,
          },
        }),
      },
      variants: [
        {
          props: { size: 'large' },
          style: ({ theme }: { theme: Theme }) => ({
            '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
              transform: `translate(${theme.spacing(1.5)}, ${theme.spacing(1.5)}) scale(1)`,
            },
            '&.MuiInputLabel-outlined.MuiInputLabel-shrink': {
              fontSize: theme.typography.micro1.fontSize,
              transform: `translate(${theme.spacing(1.5)}, -9px) scale(1)`,
            },
          }),
        },
      ],
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '&:has(.MuiInputLabel-shrink) .MuiOutlinedInput-notchedOutline legend > span': {
            paddingInline: '0.25em',
          },
        },
      },
      variants: [
        {
          props: { size: 'large' },
          style: {
            '&:has(.MuiInputLabel-shrink) .MuiOutlinedInput-notchedOutline legend > span': {
              paddingInline: '0.35em',
            },
          },
        },
      ],
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          marginLeft: theme.spacing(1.5),
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popupIndicator: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        clearIndicator: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-root': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            '& .MuiAutocomplete-input': { padding: outlinedInputPadding(theme) },
          },
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            '& .MuiAutocomplete-input': { padding: outlinedInputPaddingSmall(theme) },
          },
          '& .MuiOutlinedInput-root.MuiInputBase-sizeLarge': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: 0,
            '& .MuiAutocomplete-input': { padding: outlinedInputPaddingLarge(theme) },
          },
          '& .MuiOutlinedInput-root:has(.MuiInputAdornment-positionStart)': {
            paddingLeft: theme.spacing(1),
            '& .MuiAutocomplete-input': { paddingLeft: 0 },
          },
        }),
        paper: ({ theme }) => menuPaperStyles(theme),
        listbox: ({ theme }) => ({
          ...menuListStyles(theme),
          padding: theme.spacing(0.75),
          '& .MuiAutocomplete-option': {
            ...listItemStyles(theme),
            '&[aria-selected="true"]': {
              backgroundColor: theme.palette.action.selected,
              '&.Mui-focused, &.Mui-focusVisible': {
                backgroundColor: theme.palette.action.selected,
              },
            },
            '&:has(.MuiCheckbox-root)[aria-selected="true"]': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              '&.Mui-focused, &.Mui-focusVisible': {
                backgroundColor: alpha(theme.palette.primary.main, 0.09),
              },
            },
          },
        }),
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          lineHeight: 1,
          '& .MuiTypography-root': { lineHeight: 1 },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiInputBase-multiline': { padding: outlinedInputPadding(theme) },
          '&.MuiInputBase-multiline.MuiInputBase-sizeSmall': {
            padding: outlinedInputPaddingSmall(theme),
          },
          '&.MuiInputBase-multiline.MuiInputBase-sizeLarge': {
            padding: outlinedInputPaddingLarge(theme),
          },
          '&.MuiInputBase-multiline .MuiOutlinedInput-input': { padding: 0 },
          '&:not(.Mui-error):not(.Mui-disabled):not(.Mui-focused):hover .MuiOutlinedInput-notchedOutline':
            {
              borderColor: theme.palette.action.active,
            },
          '&.Mui-error:not(.Mui-disabled)': {
            '& .MuiSelect-icon, & .MuiAutocomplete-popupIndicator, & .MuiInputAdornment-root': {
              color: theme.palette.error.main,
            },
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          '&:not(.Mui-error).Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.accent.main,
          },
          '&:has(.MuiInputAdornment-positionStart)': {
            paddingLeft: theme.spacing(1),
            '& .MuiOutlinedInput-input': { paddingLeft: 0 },
          },
          '&:has(.MuiInputAdornment-positionEnd)': {
            paddingRight: theme.spacing(1),
            '& .MuiOutlinedInput-input': { paddingRight: 0 },
          },
        }),
        input: ({ theme }) => ({ padding: outlinedInputPadding(theme) }),
        inputSizeSmall: ({ theme }) => ({ padding: outlinedInputPaddingSmall(theme) }),
        notchedOutline: ({ theme }) => ({
          '& legend': { fontSize: `${INPUT_LABEL_SHRINK_SCALE}em` },
          borderColor: theme.palette.custom.border.primary,
        }),
      },
      variants: [
        {
          props: { size: 'large' },
          style: ({ theme }: { theme: Theme }) => ({
            '& .MuiOutlinedInput-input': { padding: theme.spacing(1.35, 1.5) },
          }),
        },
      ],
    },
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 230,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },
};
